import { sdk } from '@/lib/sdk'

export interface PickableProduct {
  id: string
  title: string
  type: string
  status: string
  price: string | number
  currency?: string
  billingInterval?: string | null
  trialDays?: number | null
  /** Non-empty makes it an add-on (F-33): sold on top of a plan, never alone. */
  prerequisiteGroupIds?: string[]
  /** Plans that take it as a billing option (F-29 phase 3). */
  subscriptionGroups?: Array<{ id: string; name?: string | null }>
}

export interface PickablePlan {
  id: string
  name: string
  description?: string | null
  sortOrder?: number
  /** F-29 phase 3: a `link_only` plan is hidden from the store even when picked. */
  visibility?: 'public' | 'link_only'
  /** The plan's billing options, for display; the plan is picked whole. */
  options: PickableProduct[]
}

/** The API caps a page at 50; a store picks from every product the community has. */
const PAGE_SIZE = 50

export const loadAllProducts = async (): Promise<PickableProduct[]> => {
  const all: PickableProduct[] = []
  for (let page = 1; page <= 20; page += 1) {
    const response: unknown = await sdk.products.list({ page, limit: PAGE_SIZE })
    const value = response as { items?: PickableProduct[]; pagination?: { totalPages?: number } } | PickableProduct[]
    const items = Array.isArray(value) ? value : value?.items ?? []
    all.push(...items)
    const totalPages = Array.isArray(value) ? 1 : value?.pagination?.totalPages ?? 1
    if (page >= totalPages || items.length === 0) break
  }
  return all
}

/** Every plan of the community, with its billing options matched from the products. */
export const loadPickablePlans = async (products: PickableProduct[]): Promise<PickablePlan[]> => {
  const response: unknown = await sdk.subscriptionGroups.list()
  interface PlanListRow {
    id: string
    name: string
    description?: string | null
    sortOrder?: number
    visibility?: string
    isActive?: boolean
  }
  const groups = (Array.isArray(response) ? response : (response as { items?: PlanListRow[] })?.items ?? []) as PlanListRow[]
  return groups
    .filter((group) => group.isActive !== false)
    .map((group): PickablePlan => ({
      id: group.id,
      name: group.name,
      description: group.description ?? null,
      sortOrder: group.sortOrder ?? 0,
      visibility: group.visibility === 'link_only' ? 'link_only' : 'public',
      options: products.filter((product) => product.subscriptionGroups?.some((g) => g.id === group.id)),
    }))
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
}

/** One-offs: what the store sells on the spot (F-29 phase 3, criterion 1). */
export const oneOffProducts = (products: PickableProduct[]): PickableProduct[] =>
  products.filter((product) => product.type !== 'SUBSCRIPTION')

/** Add-ons: what is only sold on top of a plan (F-29 phase 3, criteria 5-8). */
export const addOnProducts = (products: PickableProduct[]): PickableProduct[] =>
  products.filter((product) => (product.prerequisiteGroupIds?.length ?? 0) > 0)

/** Billing options of no plan: never pickable alone (criteria 4 and 11). */
export const orphanOptions = (products: PickableProduct[]): PickableProduct[] =>
  products.filter(
    (product) => product.type === 'SUBSCRIPTION' && !product.prerequisiteGroupIds?.length && !product.subscriptionGroups?.length
  )
